import React from "react";
import { History, MessageSquareText, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { DiscussionWithoutContentDTO } from "@/generated-client";
import { ClientFactory } from "@/main/common/ClientFactory";
import { logApiFailure } from "@/main/common/ApiUtils";
import { navigateToUrl } from "@/main/common/BrowserNavigation";
import { Localizer } from "@/main/react/Types";
import { PaginatedListWrapperV2 } from "@/main/react/list/PaginatedListWrapperV2";
import { createStandardPaginatedStatusSlots, PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import { showYesNoDialog } from "@/main/react/Dialog/YesNoDialog";
import { RecentlyViewedDiscussionCards } from "./RecentlyViewedDiscussionRail";

// SV-135 S6 (D5): inside the rail panel, rows are separated by their own padding, never by inner
// dividers. `HOME_RAIL_ROW_LIST_CLASS` (HomePageShared.ts) keeps its hairlines for the discussion page's
// rail, which is S10's to quiet.
const HOME_RAIL_ROW_STACK_CLASS = "flex flex-col";

// The page's rows are standalone cards (each carries its own Remove and bookmark footer), so they are
// separated by a gap rather than by padding inside one panel.
const HISTORY_PAGE_ROW_STACK_CLASS = "flex flex-col gap-3";

// The home discovery bootstrap seeds the history tab with this many items; when the
// preview is full there may be more, so we offer a "See all" expansion that pages the
// dedicated recently-viewed endpoint from the already-loaded items onward.
const HISTORY_PREVIEW_SIZE = 4;
const HISTORY_FULL_PAGE_SIZE = 20;

const NO_ITEMS: DiscussionWithoutContentDTO[] = [];

/**
 * Recently-viewed history surface, in two modes.
 *
 * - **`rail`** (the default) — the home discovery rail. Shows the seeded preview cards and, when the
 *   preview is full, a "See all" control that expands into a cursor-paginated list backed by the
 *   existing recently-viewed endpoint. The already loaded preview items are reused as the first page
 *   (no refetch).
 * - **`page`** — `/my_dashboard/discussions/history` (QoL work order D4 / A10, slice `2b`). Fetches its
 *   own first page, and adds the two edits wave 1's server half made possible: a per-row "Remove from
 *   history" (`DELETE /ajax/reading-history/{discussionPublicId}`) and a confirmed "Clear history"
 *   (`DELETE /ajax/reading-history`). Both are a per-member HIDE on the server; `discussion_views` rows
 *   are never deleted, so author read counts and seen-state do not move.
 *
 * One `PaginatedListWrapperV2` call site serves both, and it stays out of Back restoration
 * (`restorationKeyWiring.spec.ts`): a removed row leaves through `setItems`, with no token in any key.
 */
export function RecentlyViewedHistoryPanel(props: {
    /** The rail's seeded preview. Ignored in `page` mode, which fetches its own first page. */
    items?: DiscussionWithoutContentDTO[];
    localizer: Localizer;
    mode?: "rail" | "page";
}) {
    const { localizer, mode = "rail" } = props;
    const items = props.items ?? NO_ITEMS;
    const isPage = mode === "page";
    const [expanded, setExpanded] = React.useState(false);
    const [removingIds, setRemovingIds] = React.useState<ReadonlySet<string>>(() => new Set());
    const paginationApi = React.useMemo(() => ClientFactory.getAjaxPaginationRestControllerApi(), []);

    const fetchPage = React.useCallback(async (paginationParameter?: string): Promise<DiscussionWithoutContentDTO[]> => {
        const { data } = await paginationApi.recentlyViewedDiscussions(paginationParameter, HISTORY_FULL_PAGE_SIZE);
        return data ?? [];
    }, [paginationApi]);

    const getId = React.useCallback((discussion: DiscussionWithoutContentDTO) => {
        return discussion.publicId
            ?? discussion.slug
            ?? (discussion.paginationParameter != null ? String(discussion.paginationParameter) : undefined);
    }, []);

    const removeEntry = React.useCallback(async (
        discussion: DiscussionWithoutContentDTO,
        setItems: React.Dispatch<React.SetStateAction<DiscussionWithoutContentDTO[]>>,
    ) => {
        const publicId = discussion.publicId;
        if (!publicId || removingIds.has(publicId)) {
            return;
        }
        setRemovingIds((previous) => new Set(previous).add(publicId));
        try {
            await ClientFactory.getAjaxReadingHistoryRestControllerApi().removeReadingHistoryEntry(publicId);
            setItems((previous) => previous.filter((item) => item.publicId !== publicId));
            toast.success(localizer("reading_history.removed"));
        } catch (error) {
            logApiFailure("[RecentlyViewedHistoryPanel] remove from reading history failed", error);
            toast.error(localizer("shared.action.failed"));
        } finally {
            setRemovingIds((previous) => {
                const next = new Set(previous);
                next.delete(publicId);
                return next;
            });
        }
    }, [localizer, removingIds]);

    const confirmClear = React.useCallback((
        setItems: React.Dispatch<React.SetStateAction<DiscussionWithoutContentDTO[]>>,
    ) => {
        showYesNoDialog({
            localizer,
            title: localizer("reading_history.clear_confirm.title"),
            message: <p>{localizer("reading_history.clear_confirm.body")}</p>,
            yesLabel: localizer("reading_history.clear"),
            yesIcon: Trash2,
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            destructive: true,
            yesPlaywrightId: "reading-history-clear-confirm",
            noPlaywrightId: "reading-history-clear-cancel",
            // A rejection is the dialog's to report: it keeps itself open with its own localized
            // banner, so nothing here swallows the error or toasts a second message.
            onYes: async () => {
                await ClientFactory.getAjaxReadingHistoryRestControllerApi().clearReadingHistory();
                setItems([]);
                toast.success(localizer("reading_history.cleared"));
            },
        });
    }, [localizer]);

    if (!isPage && !expanded) {
        const canSeeAll = items.length >= HISTORY_PREVIEW_SIZE;

        return (
            <div>
                <RecentlyViewedDiscussionCards
                    items={items}
                    localizer={localizer}
                    itemPlaywrightId="home-continue-reading-card"
                    className={HOME_RAIL_ROW_STACK_CLASS}
                    variant="flush"
                />
                {canSeeAll ? (
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        // SV-135 S6: a full-width row in primary text with the hover wash, not a bordered pill.
                        className="mt-1 flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-primary transition-colors hover:bg-muted/45 [@media(pointer:coarse)]:min-h-11"
                        data-playwright-id="home-continue-reading-see-all"
                    >
                        {localizer("home.continue_reading.see_all")}
                    </button>
                ) : null}
            </div>
        );
    }

    return (
        <PaginatedListWrapperV2<DiscussionWithoutContentDTO, string>
            // The page fetches its own first page; the rail reuses its seeded preview as page one.
            initialItems={isPage ? undefined : items}
            fetchPage={fetchPage}
            getId={getId}
            paginationMode="button"
            statusSlots={createStandardPaginatedStatusSlots({
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                errorMessage: localizer("home.discovery.load_failed"),
                retryLabel: localizer("literal.retry"),
                // T6: say what fills this list and offer the way there; no apology, no machinery.
                empty: isPage ? (
                    <PaginatedListEmptyState
                        icon={History}
                        titleHeadingLevel={2}
                        title={localizer("reading_history.empty")}
                        action={{
                            label: localizer("literal.explore_discussions"),
                            onClick: () => navigateToUrl("/", { source: "dashboard-empty-reading-history" }),
                            icon: MessageSquareText,
                        }}
                    />
                ) : undefined,
            })}
            render={({ items: loadedItems, hasMore, loading, onLoadMore, setItems }) => (
                <div>
                    {isPage && loadedItems.length > 0 ? (
                        <div className="mb-3 flex justify-end">
                            <button
                                type="button"
                                onClick={() => confirmClear(setItems)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-text-main transition-colors hover:bg-muted [@media(pointer:coarse)]:min-h-11"
                                data-playwright-id="reading-history-clear"
                            >
                                <Trash2 className="size-4" aria-hidden="true" />
                                {localizer("reading_history.clear")}
                            </button>
                        </div>
                    ) : null}
                    <RecentlyViewedDiscussionCards
                        items={loadedItems}
                        localizer={localizer}
                        itemPlaywrightId={isPage ? "reading-history-row" : "home-continue-reading-card"}
                        className={isPage ? HISTORY_PAGE_ROW_STACK_CLASS : HOME_RAIL_ROW_STACK_CLASS}
                        variant={isPage ? "card" : "flush"}
                        analyticsSurface={isPage ? "reading_history_page" : undefined}
                        onRemove={isPage ? (discussion) => void removeEntry(discussion, setItems) : undefined}
                        removingIds={isPage ? removingIds : undefined}
                    />
                    {hasMore ? (
                        <button
                            type="button"
                            onClick={() => void onLoadMore()}
                            disabled={loading}
                            className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-text-main transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-70 [@media(pointer:coarse)]:min-h-11"
                            data-playwright-id={isPage ? "reading-history-load-more" : "home-continue-reading-load-more"}
                        >
                            {localizer("home.continue_reading.load_more")}
                        </button>
                    ) : null}
                </div>
            )}
        />
    );
}
